// FE-PINV-064 — Filter-counts REST client.
//
// Targets BE-PINV-062's ``GET /api/v1/sales/pipeline/filter-counts``
// endpoint. Per spec the BE returns excluding-self counts: with
// ``cadence=[critical]`` active, the ``cadence`` bucket shows the
// would-be counts as if cadence had not been applied. The FE
// requests one bucket at a time via ``exclude_facet`` so each
// facet's options can be queried with the right exclusion.
//
// W2 mocks this until W1 lands BE-PINV-062. The host (W1's shell)
// will wire the real fetch through the same hook.

import { api } from "@/api/client";

import type { PipelineFacetKey, PipelineFilters } from "./filterState";


export type FilterCountsBucket = Record<string, number>;


export type FilterCountsResponse = {
  [K in PipelineFacetKey]?: FilterCountsBucket;
};


function pushIfNonEmpty(
  out: URLSearchParams, key: string, value: string,
): void {
  if (value) out.set(key, value);
}


export function buildFilterCountsParams(
  filters: PipelineFilters,
  excludeFacet: PipelineFacetKey | null,
  showClosed: boolean,
): URLSearchParams {
  const out = new URLSearchParams();
  pushIfNonEmpty(out, "cadence", filters.cadence.join(","));
  pushIfNonEmpty(out, "stage", filters.stage.join(","));
  pushIfNonEmpty(out, "lives_band", filters.lives_band.join(","));
  if (filters.renewal_window) {
    out.set("renewal_window", filters.renewal_window);
  }
  pushIfNonEmpty(
    out, "opportunity_type", filters.opportunity_type.join(","),
  );
  pushIfNonEmpty(out, "producer", filters.producer.join(","));
  if (filters.alerts_only) out.set("alerts_only", "true");
  if (showClosed) out.set("show_closed", "true");
  if (excludeFacet) out.set("exclude_facet", excludeFacet);
  return out;
}


export function fetchFilterCounts(
  filters: PipelineFilters,
  options: {
    excludeFacet: PipelineFacetKey | null;
    showClosed: boolean;
    signal?: AbortSignal;
  },
): Promise<FilterCountsResponse> {
  const params = buildFilterCountsParams(
    filters, options.excludeFacet, options.showClosed,
  );
  return api.get<FilterCountsResponse>(
    `/api/v1/sales/pipeline/filter-counts?${params.toString()}`,
    { signal: options.signal },
  );
}
